import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faClock } from "@fortawesome/free-regular-svg-icons";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { Row, Col, message } from "antd";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_CLASS_DETAILS from "graphql/queries/GetClassDetails";

// components
import Button from "common/button";
import ClassList from "common/class_card/ClassList";
import CancelModal from "common/modal/cancel_modal";
import NoMeetingLinkModal from "common/modal/no_meeting_link_modal";

// hooks
import { Trans, useTranslation } from "react-i18next";
import useCountdown from "hooks/useCountdown";
import useModal from "hooks/useModal";

// utils and styles
import { Lesson } from "types";
import screenSize from "utils/screenSize";
import BannerTitleTimeCountdown from "./BannerTitleTimeCountdown";
import {
  BannerWrapper,
  BannerTitle,
  BannerContent,
  BannerIcon,
  BannerTextAndButton,
  BannerInstruction,
  CancelButton,
  ImmediateCancelButton,
} from "./styles";

// LEGACY CSS
import "pages/class_details/ClassDetails.css";
import ClassParticipants from "./ClassParticipants";

type TProps = {
  currentClass: Lesson;
};

const BookedClassBanner = ({ currentClass }: TProps) => {
  const { t } = useTranslation("class_banner");
  const [wherebyLink, setWherebyLink] = useState(currentClass.wherebyLink);

  const { data: currentUserInfo } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
  });

  const timeLeft = useCountdown(currentClass.startTime, false); // time left until start time of class
  const timeSinceBooked = useCountdown(currentClass.bookedAt, true);
  const [cancelModalVisible, showModal, closeModal] = useModal();
  const [noMeetingModalVisible, showNoMeetingModal, closeNoMeetingModal] =
    useModal();

  // Update local state when currentClass changes
  useEffect(() => {
    if (currentClass.wherebyLink) {
      setWherebyLink(currentClass.wherebyLink);
    }
  }, [currentClass.wherebyLink]);

  // Query to fetch class details
  const { data: classData, refetch } = useQuery(GET_CLASS_DETAILS, {
    variables: { id: currentClass.id },
    fetchPolicy: "network-only",
  });

  // Update wherebyLink when new data arrives
  useEffect(() => {
    if (classData?.classDetails?.currentClass?.wherebyLink) {
      setWherebyLink(classData?.classDetails?.currentClass?.wherebyLink);
    }
  }, [classData]);

  const renderIcon = () => {
    const ONE_DAYS_IN_SECONDS = 1 * 24 * 60 * 60;
    if (timeLeft.totalSecondsLeft > ONE_DAYS_IN_SECONDS) {
      return faCalendarAlt;
    }
    if (timeLeft.totalSecondsLeft > 0) {
      return faClock;
    }
    // time's up
    return faExclamationCircle;
  };

  const renderPrepareClassButton = () => {
    // Open support page in new tab
    const handleClick = () => window.open(t("SUPPORT_PAGE_URL"), "_blank");

    return (
      <Button type="primary" onClick={() => handleClick()}>
        {t("PREPARE CLASS").toUpperCase()}
      </Button>
    );
  };

  const renderJoinClassButton = () => {
    const handleClick = async () => {
      const userName =
        currentUserInfo?.me?.givenName || currentUserInfo?.me?.email;

      if (!currentClass.teacher) {
        message.error(
          t("Cannot join this class because there's no teacher assigned yet.")
        );
      } else if (!wherebyLink) {
        // Try to refetch the data
        try {
          await refetch();
          // Show modal if still no link after a short delay
          setTimeout(() => {
            if (!wherebyLink) {
              showNoMeetingModal();
            }
          }, 1000);
        } catch (error) {
          showNoMeetingModal();
        }
      } else {
        // Add displayName parameter to the Whereby link
        const wherebyUrl = new URL(wherebyLink);
        wherebyUrl.searchParams.set("displayName", userName);
        wherebyUrl.searchParams.set("leaveButton", "off");
        window.open(wherebyUrl.toString(), "_blank");
      }
    };

    return (
      <Button type="primary" onClick={() => handleClick()}>
        {t("JOIN CLASS").toUpperCase()}
      </Button>
    );
  };

  const renderBannerButton = () => {
    const FIVE_MINS_IN_SECONDS = 60 * 5;
    if (timeLeft.totalSecondsLeft > FIVE_MINS_IN_SECONDS) {
      return renderPrepareClassButton();
    }
    return renderJoinClassButton();
  };

  const hasClassNotStarted = () => {
    return timeLeft.totalSecondsLeft > 0;
  };

  const renderCancelButtonandText = () => {
    const MIN_30_IN_SECONDS = 30 * 60;
    const buttonText =
      timeSinceBooked.totalSecondsLeft < MIN_30_IN_SECONDS ? (
        <ImmediateCancelButton>{t("IMMEDIATE CANCEL")}</ImmediateCancelButton>
      ) : (
        <CancelButton>{t("CANCEL")}</CancelButton>
      );

    const handleClick = () => showModal();

    return (
      <div
        className="cancel-container"
        style={{
          flexDirection: "column-reverse",
          alignItems: "flex-start",
        }}
      >
        <Button
          className="cancel-button"
          name="cancleClass"
          type="primary"
          onClick={() => handleClick()}
        >
          {buttonText}
        </Button>
        <span className="cancel-info">
          <Trans
            t={t}
            i18nKey="You can cancel anytime. Credits are refunded if you cancel within 30 minutes of booking or at least 24 hours before class and before the credit expires."
            components={[<strong />]}
          />
        </span>
      </div>
    );
  };

  return (
    <BannerWrapper>
      <Row gutter={24}>
        <Col xs={24} sm={12} md={16} lg={18}>
          <BannerContent>
            <BannerIcon>
              <FontAwesomeIcon icon={renderIcon() as IconProp} size="2x" />
            </BannerIcon>
            <BannerTextAndButton>
              <BannerTitle>{BannerTitleTimeCountdown(timeLeft)}</BannerTitle>
              {screenSize() === "desktop" && renderBannerButton()}
            </BannerTextAndButton>
          </BannerContent>

          {screenSize() === "mobile" && (
            <div>
              <br />
              {renderBannerButton()}
            </div>
          )}

          <BannerInstruction>
            {t(
              "Five minutes before your class starts the “Join Class” button will appear here. Click it to enter the meeting link. If you need support email us at"
            )}{" "}
            <a
              href="mailto:support@flexiclasses.com"
              style={{ color: "white" }}
            >
              support@flexiclasses.com
            </a>
          </BannerInstruction>

          <ClassParticipants
            participants={currentClass.participants}
            className="participants"
          />

          {hasClassNotStarted() && renderCancelButtonandText()}
        </Col>
        <ClassList lessons={[currentClass]} dynamicFooter />
      </Row>

      <CancelModal
        visible={cancelModalVisible}
        onCloseModal={() => closeModal()}
        classId={currentClass.id}
      />
      <NoMeetingLinkModal
        visible={noMeetingModalVisible}
        onCloseModal={closeNoMeetingModal}
      />
    </BannerWrapper>
  );
};

export default BookedClassBanner;
